/**
 * =============================================================================
 *  PHAM VI CHINH SUA - chon cai gi khi bam vao mot khoi
 * =============================================================================
 *
 *  VAN DE DA DO DUOC, khong phai gia dinh:
 *
 *  Tren bao gia va hop dong tieng Viet (nhieu bang), viec gom nhom keo CA MOT
 *  BANG vao MOT khoi. Do that te tren trang 1 cua mot bao gia that: 60 doan chu
 *  chi cho ra HAI khoi sua duoc - mot khoi 173 ky tu phu ca bang tom tat, va mot
 *  khoi 542 ky tu phu ca bang chi tiet (dong tieu de + cac dong hang + gia, tron
 *  lam mot). Nguoi dung muon sua MOT o gia se mo ra mot o soan chua ca bang duoi
 *  dang mot chuoi lien tuc, va ap dung se thay ca bang bang mot doan van duy nhat.
 *
 *  Nguyen nhan da do, KHONG can tranh luan lai:
 *    - Quet nguong wordGap cua pha 1 tu 2.5 xuong 0.3 gan nhu khong doi gi
 *      (trung vi so khoi/trang van la 16; khoi lon nhat tren mot trang tieu bieu
 *      chi tu 326 ky tu xuong 288). wordGap KHONG phai nguyen nhan.
 *    - Pha 1 do khoang cach voi HOP BAO DANG LON DAN cua dong chu khong phai voi
 *      run gan nhat, nen cac o bang dinh vao nhau bac cau: A-B gan va B-C gan thi
 *      A,B,C thanh mot dong ke ca khi A va C rat xa nhau.
 *    - Pha 2 xep cac dong day-trang do thanh mot khoi vi cac dong bang lien tiep
 *      cung font, cung co chu, buoc duong co so deu, va chong lan ngang ~100% -
 *      dung y het cac dau hieu nhan dang mot doan van.
 *
 *  Nhan dang bang / cot cho dung (chieu do khoang trang doc, hoac mot mo hinh bo
 *  cuc) la viec that va thuoc ve LOI RUST, kem do dac tren kho tai lieu. KHONG
 *  phai viec cua tep nay.
 *
 *  GIAI PHAP O DAY: khong dong vao thuat toan gom nhom mot dong nao. Doi thu
 *  nguoi dung CHON. Bam vao mot khoi se chon DON VI NHO NHAT nam duoi con tro:
 *
 *      run  ("Ô")       thuong la mot o bang hoac mot cum tu
 *        -> lui ve line ("Dòng")
 *        -> lui ve block ("Cả khối")
 *
 *  Day dung la cach Acrobat cu xu: bam mot cai chon mot DOI TUONG van ban, khong
 *  phai ca vung van ban cua trang.
 *
 * =============================================================================
 *  DIEU QUAN TRONG NHAT CUA TEP NAY
 * =============================================================================
 *  scopeAsBlock() dung mot TextBlock TONG HOP mang bbox va cac run CUA RIENG
 *  pham vi da chon. Toan bo duong ghi (deriveBlockGeometry -> hinh chu nhat che,
 *  layoutInBox -> hop dat chu, firstBaselineY -> duong co so) deu suy ra tu chinh
 *  doi tuong do. Neu co cho nao cua duong ghi lay lai bbox cua khoi CHA thi hinh
 *  chu nhat che se phu ca bang tro lai - tuc la dung lai chinh loi dang duoc sua.
 *  Test trong apps/web/test/scope.test.ts giu cho dieu do khong xay ra.
 */
import type { PdfPoint, PdfRect, TextBlock, TextRun } from '@pdfeditor/types';
import { worstEditability } from '../fontDiagnostics';

// ---------------------------------------------------------------------------
// Hinh hoc dung chung
// ---------------------------------------------------------------------------

/** Hop bao cua hai hinh chu nhat. Dung chung voi duong gom nhom trong PdfCoreMock. */
export function unionRect(a: PdfRect, b: PdfRect): PdfRect {
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x + a.width, b.x + b.width);
  const y1 = Math.max(a.y + a.height, b.y + b.height);
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

function containsPoint(rect: PdfRect, p: PdfPoint, padPt: number): boolean {
  return (
    p.x >= rect.x - padPt &&
    p.x <= rect.x + rect.width + padPt &&
    p.y >= rect.y - padPt &&
    p.y <= rect.y + rect.height + padPt
  );
}

// ---------------------------------------------------------------------------
// Gom run thanh dong - MOT dinh nghia duy nhat
// ---------------------------------------------------------------------------

/** Mot dong: cac run cung duong co so trong sai so. */
export interface RunLine {
  runs: TextRun[];
  baseline: number;
  bbox: PdfRect;
  /** Co chu lon nhat trong dong - pha 2 cua viec gom nhom dung no lam buoc do. */
  lineHeight: number;
}

/**
 * PHA 1 CUA VIEC GOM NHOM: gom cac run co cung duong co so thanh dong.
 *
 * Ham nay la NOI DUY NHAT dinh nghia phep gom do. PdfCoreMock.textBlocks() goi
 * no de dung khoi, va tang giao dien goi lai chinh no de bam trung don vi
 * "Dòng". Hop dong kieu trong packages/types khong cho TextBlock mang danh sach
 * dong (va ta khong duoc sua hop dong do), nen cach duy nhat de hai ben khong
 * lech nhau la dung CHUNG MOT HAM chu khong phai chep lai nguong sang cho khac.
 *
 * Nguong o day KHONG DOI so voi ban truoc: tolerance = max(1.5, coChu * 0.5).
 */
export function groupRunsIntoLines(runs: readonly TextRun[]): RunLine[] {
  // Sap xep lai de ham la mot ham thuan: goi tren block.runs (da theo thu tu
  // doc) hay tren mot mang chua sap deu cho cung ket qua.
  const sorted = [...runs].sort((a, b) => {
    const dy = b.matrix[5] - a.matrix[5];
    if (Math.abs(dy) > 1) return dy;
    return a.matrix[4] - b.matrix[4];
  });

  const lines: RunLine[] = [];
  for (const run of sorted) {
    const baseline = run.matrix[5];
    const last = lines[lines.length - 1];
    const tolerance = Math.max(1.5, run.fontSizePt * 0.5);
    if (last && Math.abs(last.baseline - baseline) <= tolerance) {
      last.runs.push(run);
      last.bbox = unionRect(last.bbox, run.bbox);
      last.lineHeight = Math.max(last.lineHeight, run.fontSizePt);
    } else {
      lines.push({
        runs: [run],
        baseline,
        bbox: { ...run.bbox },
        lineHeight: run.fontSizePt,
      });
    }
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Pham vi
// ---------------------------------------------------------------------------

export type ScopeKind = 'run' | 'line' | 'block';

/** Nhan hien cho nguoi dung. Tieng Viet, co dau. */
export const SCOPE_LABEL: Record<ScopeKind, string> = {
  run: 'Ô',
  line: 'Dòng',
  block: 'Cả khối',
};

/** Giai thich ngan, hien duoi nut chon pham vi. */
export const SCOPE_HINT: Record<ScopeKind, string> = {
  run: 'Một đoạn chữ liền mạch — thường là một ô bảng hoặc một cụm từ.',
  line: 'Cả dòng: mọi ô nằm trên cùng một đường cơ sở.',
  block: 'Toàn bộ khối do việc gom nhóm tạo ra — có thể là cả một bảng.',
};

export interface EditScope {
  /** Ma pham vi. Dung LAM blockId cua lenh text.replace. Xem ghi chu ben duoi. */
  id: string;
  kind: ScopeKind;
  /** Ma cua TextBlock cha - luon la ma that do loi cap. */
  blockId: string;
  pageIndex: number;
  /**
   * Chi so run trong block.runs ma nguoi dung da bam trung. Day la cai NEO:
   * noi rong hay thu hep pham vi deu tinh lai tu no, nen "Ô -> Dòng -> Cả khối
   * -> Dòng -> Ô" luon quay ve dung o ban dau.
   */
  anchorRunIndex: number;
  /** Chi so cua don vi trong khoi (run thu may, hoac dong thu may). */
  index: number;
  /** So don vi cung cap trong khoi - giao dien hien "Ô 3/7". */
  siblingCount: number;
  runs: TextRun[];
  bbox: PdfRect;
  text: string;
  label: string;
}

/**
 * Ma pham vi, nhet vao truong blockId cua EditCommand.
 *
 * QUYET DINH CO Y THUC: hop dong kieu (packages/types) chi co
 * `text.replace { blockId }` va ta KHONG duoc sua no. Nen pham vi hep hon mot
 * khoi duoc ma hoa vao chinh chuoi ma:
 *
 *      p0-b3        ca khoi
 *      p0-b3#l2     dong thu 2 cua khoi do
 *      p0-b3#r5     run thu 5 cua khoi do
 *
 * Chuoi nay van la mot dinh danh doi tuong van ban on dinh, dung nghia cua
 * truong blockId. Loi Rust sau nay co the cap ma rieng cho tung doi tuong ma
 * khong pha vo gi - ben doc chi can parseScopeId().
 */
export function makeScopeId(blockId: string, kind: ScopeKind, index: number): string {
  if (kind === 'block') return blockId;
  return `${blockId}#${kind === 'run' ? 'r' : 'l'}${index}`;
}

const SCOPE_SUFFIX = /^(.*)#([rl])(\d+)$/;

/** Tach ma pham vi. Ma khong co hau to duoc hieu la ca khoi. */
export function parseScopeId(scopeId: string): {
  blockId: string;
  kind: ScopeKind;
  index: number;
} {
  const m = SCOPE_SUFFIX.exec(scopeId);
  if (!m) return { blockId: scopeId, kind: 'block', index: 0 };
  const index = Number(m[3]);
  return {
    blockId: m[1] ?? scopeId,
    kind: m[2] === 'r' ? 'run' : 'line',
    index: Number.isInteger(index) && index >= 0 ? index : 0,
  };
}

/**
 * Van ban cua mot day run, ghep lai.
 *
 * DINH NGHIA DUY NHAT trong ca ung dung. Duoc dung khi dien san o soan, khi
 * dung previousText cho lenh, va khi quyet dinh co ve lop chinh sua de len hay
 * khong. Ba cho phai dung CUNG mot chuoi: neu khong, Ctrl+Z se de lai mot mang
 * che trang mang noi dung y het ban goc.
 */
export function runsPlainText(runs: readonly TextRun[]): string {
  return runs.map((r) => r.text).join('');
}

function bboxOfRuns(runs: readonly TextRun[]): PdfRect | null {
  const first = runs[0];
  if (!first) return null;
  let box: PdfRect = { ...first.bbox };
  for (let i = 1; i < runs.length; i++) box = unionRect(box, runs[i]!.bbox);
  return box;
}

/**
 * Dung mot pham vi tu khoi + run neo + cap do mong muon.
 * Tra ve null khi khoi khong co du lieu cho cap do do.
 */
export function scopeFor(
  block: TextBlock,
  anchorRunIndex: number,
  kind: ScopeKind,
): EditScope | null {
  if (block.runs.length === 0) return null;
  const anchor = Math.min(Math.max(0, anchorRunIndex), block.runs.length - 1);

  if (kind === 'block') {
    return {
      id: makeScopeId(block.id, 'block', 0),
      kind: 'block',
      blockId: block.id,
      pageIndex: block.pageIndex,
      anchorRunIndex: anchor,
      index: 0,
      siblingCount: 1,
      runs: [...block.runs],
      // Bbox cua khoi lay NGUYEN tu loi, khong tinh lai tu run: hai gia tri co
      // the lech nhau, va bbox cua loi la thu duong ghi da luon dung.
      bbox: block.bbox,
      text: runsPlainText(block.runs),
      label: SCOPE_LABEL.block,
    };
  }

  if (kind === 'run') {
    const run = block.runs[anchor];
    if (!run) return null;
    const bbox = bboxOfRuns([run]);
    if (!bbox) return null;
    return {
      id: makeScopeId(block.id, 'run', anchor),
      kind: 'run',
      blockId: block.id,
      pageIndex: block.pageIndex,
      anchorRunIndex: anchor,
      index: anchor,
      siblingCount: block.runs.length,
      runs: [run],
      bbox,
      text: run.text,
      label: SCOPE_LABEL.run,
    };
  }

  // kind === 'line'
  const lines = groupRunsIntoLines(block.runs);
  const target = block.runs[anchor];
  if (!target) return null;
  const lineIndex = lines.findIndex((l) => l.runs.includes(target));
  const line = lines[lineIndex];
  if (!line) return null;
  const bbox = bboxOfRuns(line.runs);
  if (!bbox) return null;
  return {
    id: makeScopeId(block.id, 'line', lineIndex),
    kind: 'line',
    blockId: block.id,
    pageIndex: block.pageIndex,
    anchorRunIndex: anchor,
    index: lineIndex,
    siblingCount: lines.length,
    runs: [...line.runs],
    bbox,
    text: runsPlainText(line.runs),
    label: SCOPE_LABEL.line,
  };
}

/**
 * Doc lai mot pham vi tu ma cua no.
 *
 * Duong ghi goi ham nay, nen no phai chiu duoc mot ma tro toi don vi khong con
 * ton tai (tai lieu duoc phan tich lai, so run doi): tra ve null va ben goi tu
 * choi kem ly do, thay vi lang le sua nham don vi khac.
 */
export function resolveScope(block: TextBlock, scopeId: string): EditScope | null {
  const parsed = parseScopeId(scopeId);
  if (parsed.blockId !== block.id) return null;

  if (parsed.kind === 'block') return scopeFor(block, 0, 'block');
  if (parsed.kind === 'run') {
    if (parsed.index >= block.runs.length) return null;
    return scopeFor(block, parsed.index, 'run');
  }

  const lines = groupRunsIntoLines(block.runs);
  const line = lines[parsed.index];
  if (!line) return null;
  const firstRun = line.runs[0];
  if (!firstRun) return null;
  const anchor = block.runs.indexOf(firstRun);
  return scopeFor(block, anchor < 0 ? 0 : anchor, 'line');
}

/** Khoang cach tu mot diem toi hinh chu nhat (0 khi diem nam trong). */
function distanceToRect(rect: PdfRect, p: PdfPoint): number {
  const dx = Math.max(rect.x - p.x, 0, p.x - (rect.x + rect.width));
  const dy = Math.max(rect.y - p.y, 0, p.y - (rect.y + rect.height));
  return Math.hypot(dx, dy);
}

/**
 * BAM VAO MOT KHOI LUON CHON DUNG MOT O (run) - khong bao gio ca dong hay ca khoi.
 *
 * Truoc day bam truot ra khoang trong se lui ve 'line' roi 'block'. Nguoi dung
 * bao dieu do gay hoang mang: mot cu bam nham lai mo ra o soan chua NGUYEN CA
 * BANG, va ho khong hieu vi sao. Gio moi cu bam trong hop bao khoi deu ve DUNG
 * MOT o: trung run nao thi o do, khong trung run nao thi o GAN NHAT. Khong con
 * mot duong nao tao ra pham vi 'line' / 'block' tu mot cu bam chuot.
 *
 * (widenScope / narrowScope van giu duoc 'line' / 'block' cho cac phep goi tuong
 * minh, nhung khong con giao dien nao goi chung - xem ghi chu o cuoi tep.)
 *
 * Diem tinh trong he toa do PDF. padPt noi rong vung bam mot chut vi hop bao
 * chu thuong sat khit va con tro chuot thi khong.
 */
export function scopeAtPoint(block: TextBlock, point: PdfPoint, padPt = 1.5): EditScope | null {
  if (block.runs.length === 0) return null;

  // 1. Trung truc tiep mot run. Duyet nguoc: run ve sau nam tren, giong tru tu ve.
  for (let i = block.runs.length - 1; i >= 0; i--) {
    const run = block.runs[i];
    if (!run || run.text.length === 0) continue;
    if (containsPoint(run.bbox, point, padPt)) {
      return scopeFor(block, i, 'run');
    }
  }

  // 2. Khong trung run nao (bam vao khoang trong giua / ngoai cac o) -> chon run
  //    GAN NHAT. Van la mot o duy nhat, khong bao gio ca dong hay ca khoi.
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < block.runs.length; i++) {
    const run = block.runs[i];
    if (!run || run.text.length === 0) continue;
    const d = distanceToRect(run.bbox, point);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  if (best < 0) return null;
  return scopeFor(block, best, 'run');
}

// ---------------------------------------------------------------------------
// Noi rong / thu hep
// ---------------------------------------------------------------------------

function widerKind(kind: ScopeKind): ScopeKind | null {
  if (kind === 'run') return 'line';
  if (kind === 'line') return 'block';
  return null;
}

function narrowerKind(kind: ScopeKind): ScopeKind | null {
  if (kind === 'block') return 'line';
  if (kind === 'line') return 'run';
  return null;
}

/**
 * Noi rong mot cap. Tra ve null khi da rong nhat.
 *
 * BO QUA cac cap KHONG THEM GI: mot dong chi co dung mot run thi "Ô" va "Dòng"
 * la cung mot vung. Nhay thang len cap thuc su rong hon, vi mot cu bam ma khong
 * thay gi doi tren man hinh la mot nut hong duoi mat nguoi dung.
 */
export function widenScope(block: TextBlock, scope: EditScope): EditScope | null {
  let kind = widerKind(scope.kind);
  while (kind) {
    const next = scopeFor(block, scope.anchorRunIndex, kind);
    if (next && next.runs.length > scope.runs.length) return next;
    kind = widerKind(kind);
  }
  return null;
}

/** Thu hep mot cap, cung quy tac bo qua cap khong doi gi. Null khi da hep nhat. */
export function narrowScope(block: TextBlock, scope: EditScope): EditScope | null {
  let kind = narrowerKind(scope.kind);
  while (kind) {
    const next = scopeFor(block, scope.anchorRunIndex, kind);
    if (next && next.runs.length < scope.runs.length) return next;
    kind = narrowerKind(kind);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Hop chua pham vi - CHI de suy ra can le
// ---------------------------------------------------------------------------

/**
 * HOP MA NGUOI DUNG NHIN THAY PHAM VI NAM TRONG.
 *
 * Ly do ton tai, do duoc chu khong nghi ra: o nac mac dinh "Ô", bbox cua pham vi
 * bang DUNG hop bao cua chinh chu do, nen inferAlign() khong con khe ho nao de
 * do va moi o deu ra 'left' - ke ca mot dong tieu de bang can giua ro rang bang
 * mat. Tren kho tai lieu that phep suy ra chi phat hien duoc can le o 3 tren 74
 * khoi vi ly do nay.
 *
 * Hop tra ve o day la thu dong vai tro "trang giay" cho phep do:
 *
 *   Cả khối  -> chinh bbox cua khoi. Khong co gi rong hon de quy chieu.
 *   Dòng     -> be ngang cua CA KHOI. Mot dong ngan nam giua mot khoi rong la
 *               bang chung can giua that.
 *   Ô        -> khoang trong GIUA HAI O BEN CANH tren cung dong. Day moi la cai
 *               "ô" ma nguoi dung thay tren giay: mot tieu de cot can giua thi
 *               can giua trong o cua no, chu khong phai trong ca hang bang. Lay
 *               ca hang bang lam moc se cho ra ket qua vo nghia - o dau hang
 *               luon ra 'left' va o cuoi hang luon ra 'right', tuc la lai dung
 *               kieu "can phai gia" ma cua chan thut dau dong treo trong
 *               layout.ts sinh ra de chan.
 *
 * Chi so ngang co y nghia. Chieu doc giu nguyen cua pham vi de ben goi khong
 * lo dung nham no vao viec khac.
 */
export function alignContainerFor(block: TextBlock, scope: EditScope): PdfRect {
  const vertical = { y: scope.bbox.y, height: scope.bbox.height };
  const blockLeft = block.bbox.x;
  const blockRight = block.bbox.x + block.bbox.width;

  if (scope.kind === 'block') return { ...scope.bbox };
  if (scope.kind === 'line') {
    return { x: blockLeft, width: Math.max(0, blockRight - blockLeft), ...vertical };
  }

  // kind === 'run': tim hai o ben canh TREN CUNG MOT DONG.
  const target = scope.runs[0];
  if (!target) return { ...scope.bbox };
  const line = groupRunsIntoLines(block.runs).find((l) => l.runs.includes(target));
  const siblings = (line?.runs ?? []).filter((r) => r !== target && r.text.trim().length > 0);

  const selfLeft = target.bbox.x;
  const selfRight = target.bbox.x + target.bbox.width;

  let left = blockLeft;
  let right = blockRight;
  for (const r of siblings) {
    const rRight = r.bbox.x + r.bbox.width;
    // Han hep dan tu hai phia. Chi tinh nhung run NAM HAN mot ben - run chong
    // lan (gap khi mot o duoc ve lam nhieu doan) khong phai la vach ngan o.
    if (rRight <= selfLeft && rRight > left) left = rRight;
    if (r.bbox.x >= selfRight && r.bbox.x < right) right = r.bbox.x;
  }

  // Khoi cha co the hep hon chinh run (bbox cua loi va cua run lech nhau). Luc
  // do khong co gi de do - tra ve chinh bbox de inferAlign cu xu y nhu truoc.
  if (!(right - left > target.bbox.width)) return { ...scope.bbox };
  return { x: left, width: right - left, ...vertical };
}

// ---------------------------------------------------------------------------
// Pham vi -> TextBlock tong hop cho duong ghi
// ---------------------------------------------------------------------------

/** Cac fontRef khac nhau, theo dung thu tu gap lan dau. */
function distinctFontRefs(runs: readonly TextRun[]): string[] {
  const seen: string[] = [];
  for (const run of runs) {
    if (!seen.includes(run.fontRef)) seen.push(run.fontRef);
  }
  return seen;
}

/**
 * Bien mot pham vi thanh TextBlock de dua vao duong ghi.
 *
 * DAY LA CHO DE HONG NEU LAM SAI. Moi con so hinh hoc ma preflight va
 * applyTextReplace dung deu suy ra tu doi tuong tra ve o day:
 *   - hinh chu nhat che        <- bbox
 *   - hop dat chu (ADR-005)    <- bbox.width / bbox.height
 *   - duong co so dong dau     <- run dau trong runs
 *   - mau chu (cua chan mau)   <- run dau trong runs
 * Nen bbox va runs BAT BUOC phai la cua pham vi, tuyet doi khong duoc muon lai
 * cua khoi cha. Lay nham bbox cua cha = hinh chu nhat che phu ca bang, tuc la
 * chinh cai loi dang duoc sua.
 *
 * `id` mang ma pham vi de moi bao loi va moi khoa trong ban do editedText deu
 * chi ve dung don vi nguoi dung da chon.
 */
export function scopeAsBlock(block: TextBlock, scope: EditScope): TextBlock {
  // Chan doan font duoc thu hep theo cac font THUC SU co trong pham vi.
  // diagnoseBlockFonts() dung mang fonts theo dung thu tu gap lan dau cua
  // fontRef, nen doi chieu duoc theo chi so. Neu hai ben lech do dai (loi khac
  // dung quy uoc khac) ta giu nguyen ca mang - bao thu ve phia canh bao thua
  // con hon bao thieu.
  const orderInBlock = distinctFontRefs(block.runs);
  const inScope = new Set(distinctFontRefs(scope.runs));
  const fonts =
    orderInBlock.length === block.fonts.length
      ? block.fonts.filter((_, i) => inScope.has(orderInBlock[i] ?? ''))
      : block.fonts;

  return {
    id: scope.id,
    pageIndex: block.pageIndex,
    runs: scope.runs,
    bbox: scope.bbox,
    editability:
      fonts.length > 0 ? worstEditability(fonts.map((f) => f.editability)) : block.editability,
    fonts,
  };
}
